import { ImpactedAddressType, Value } from '../store';
import { cloneDeep } from 'lodash';

export type AddressValidatorConfig = Partial<{
    // Country is hardcoded to Australia in ImpactedAddress, so no validator is required.
    // eslint-disable-next-line no-unused-vars
    [Property in keyof Omit<ImpactedAddressType, 'country'>]:
        AddressValidatorFieldConfig<NonNullable<ImpactedAddressType[Property]>>
}>

// TODO it would be nice for type V to be X in `{ value: X }`, rather than `{ value: X }`.
type AddressValidatorFieldConfig<V extends Value<any>> = {
    validator: (value?: V) => boolean,
    errorMessage: string
}

// https://kiama.testservicensw.net/storybook/?path=/docs/autosuggest-address--error-message
const defaultManualAddressValidatorConfig: AddressValidatorConfig = {
    streetAddress: {
        validator: value => RegExp('^.{3,}$').test(value?.value || ''),
        errorMessage: 'Enter the street address'
    },
    suburb: {
        validator: value => RegExp('^.{3,}$').test(value?.value || ''),
        errorMessage: 'Enter a suburb'
    },
    state: {
        validator: value => RegExp('^(ACT|NT|QLD|SA|TAS|VIC|WA|NSW)$').test(value?.value || ''),
        errorMessage: 'Select a state'
    },
    postcode: {
        // Value must be >= 100
        // https://auspost.com.au/business/marketing-and-communications/access-data-and-insights/address-data/postcode-data
        validator: value => RegExp('^([1-9]\\d{3}|0[1-9]\\d{2})$').test(value?.value || ''),
        errorMessage: 'Enter a postcode'
    },
    formattedAddress: {
        validator: value => RegExp('^.{4,}$').test(value?.value || ''),
        errorMessage: 'Enter the impacted address'
    }
};

export const getDefaultManualAddressValidatorConfig = () => cloneDeep(defaultManualAddressValidatorConfig);

const defaultAutoSuggestedAddressValidatorConfig: AddressValidatorConfig = {
    nswPointId: {
        validator: value => RegExp('.+').test(value?.value || ''),
        errorMessage: 'Sorry, something went wrong. Please try again later.'
    },
    validated: {
        validator: (value: Value<boolean> | undefined) => !!value?.value,
        errorMessage: 'Sorry, something went wrong. Please try again later.'
    },
    formattedAddress: {
        validator: value => RegExp('^.{4,}$').test(value?.value || ''),
        errorMessage: 'Enter the impacted address'
    }
};

export const getDefaultAutoSuggestedAddressValidatorConfig = () => cloneDeep(defaultAutoSuggestedAddressValidatorConfig);

export const validateAndAnnotateAddress = (
    config: AddressValidatorConfig,
    address: Partial<ImpactedAddressType>
): Partial<ImpactedAddressType> => {

    const newAddress = cloneDeep(address);
    Object.entries(config).forEach(([key, config]) => {
        if (!newAddress[key]) { newAddress[key] = {}; }
        const value = newAddress[key];
        if (config.validator(value)) {
            delete newAddress[key].errorMessage;
        } else {
            newAddress[key].errorMessage = config.errorMessage;
        }
    });
    return newAddress;
};

export const validateAddress = (config: AddressValidatorConfig, address?: Partial<ImpactedAddressType>): boolean => {
    if (!address) {
        return false;
    }
    let isValid = true;
    Object.entries(config).forEach(([key, config]) => {
        const value = address[key];
        if (!config.validator(value)) {
            isValid = false;
        }
    });
    return isValid;
};

export const stripAddressErrors = (address: Partial<ImpactedAddressType>): Partial<ImpactedAddressType> => {
    const newAddress = cloneDeep(address);
    Object.keys(address).forEach(key => {
        delete newAddress[key].errorMessage;
    });
    return newAddress;
};
